import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FiSearch, FiX, FiUserPlus } from 'react-icons/fi';
import Context from '../../../../context/Context';
import { inviteMembersOfDiffTeam } from '../../../../api/companyStructureApi/companyStructureApi';
import ReactNotifications, { showApiErrors } from '../../../common/reactNotifications/ReactNotifications';
import * as utilConstants from "../../../../utils/constants/utilsConstants";


const MemberInvitationModal = ({ employeesList, targetId }) => {
  const [show, setShow] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user } = useContext(Context);
  const dropdownRef = useRef(null);

  const FOCUS_RING_COLOR = 'focus:ring-[#AEF468] focus:border-[#AEF468]';
  const CHECKBOX_COLOR = 'checked:bg-[#AEF468] checked:border-[#AEF468]';

  const getMemberIdentifier = (member) => member?.user_id || member?.id;

  const availableMembers = useMemo(() => {
    return employeesList?.filter(employee => {
      const isCompanyHead = employee?.role === 'Company Head';
      const isTargetTeam = employee?.team_id === targetId;
      return !isCompanyHead && !isTargetTeam;
    }) || [];
  }, [employeesList, targetId]);

  const filteredMembers = useMemo(() => {
    return availableMembers.filter(member =>
      member.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, availableMembers]);


  const { isAllFilteredSelected, isIndeterminate } = useMemo(() => {
    if (!filteredMembers.length) return { isAllFilteredSelected: false, isIndeterminate: false };

    const filteredIds = filteredMembers.map(getMemberIdentifier);
    const selectedCount = filteredIds.filter(id => selectedMembers.includes(id)).length;

    const allSelected = selectedCount === filteredIds.length && filteredIds.length > 0;
    const indeterminate = selectedCount > 0 && selectedCount < filteredIds.length;

    return { isAllFilteredSelected: allSelected, isIndeterminate: indeterminate };
  }, [filteredMembers, selectedMembers]);


  const handleClose = () => {
    setShow(false);
    setSelectedMembers([]);
    setSearchTerm('');
    setDropdownOpen(false);
  };

  const handleShow = () => setShow(true);

  const toggleMember = (memberIdentifier) => {
    setSelectedMembers(prev =>
      prev.includes(memberIdentifier)
        ? prev.filter(id => id !== memberIdentifier)
        : [...prev, memberIdentifier]
    );
  };

  const handleToggleAll = () => {
    const filteredIds = filteredMembers.map(getMemberIdentifier);

    if (isAllFilteredSelected) {
      setSelectedMembers(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      const currentlySelectedSet = new Set(selectedMembers);
      filteredIds.forEach(id => currentlySelectedSet.add(id));
      setSelectedMembers(Array.from(currentlySelectedSet));
    }
  };

  const removeMember = (memberIdentifier) => {
    setSelectedMembers(prev => prev.filter(id => id !== memberIdentifier));
  };

  const handleSendInvitation = () => {
    const payload = {
      user_ids: selectedMembers,
      target_team_id: targetId
    };
    inviteMembersOfDiffTeam(user.company_id, payload, (res) => {
      if (res?.status === true || res?.status === 200) {
        ReactNotifications(res?.message || res?.data?.message, utilConstants.sucessState200);
        handleClose();
      } else {
        showApiErrors(res);
      }
    });
  };

  const getSelectedMemberNames = () => {
    return availableMembers
      .filter(member => selectedMembers.includes(getMemberIdentifier(member)))
      .map(member => ({
        id: getMemberIdentifier(member),
        name: member.first_name + " " + member.last_name
      }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  return (
    <div className='sm:ml-[60px]'>
      <button
        onClick={handleShow}
        className="flex items-center gap-2 py-[10px] px-[16px] h-[32px] font-semibold !rounded-[4px] sm:px-5 sm:py-2.5 bg-[#AEF468] text-black  hover:bg-[#9DE357] transition-colors text-sm sm:text-base"
      >
        <FiUserPlus size={20} className="flex-shrink-0" />
        <span className="hidden sm:inline">Invite Members</span>
        <span className="sm:hidden">Invite</span>
      </button>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="max-w-2xl"
        contentClassName="rounded-lg shadow-xl w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col"
        backdrop="static"
      >
        <Modal.Header closeButton className="p-4 sm:p-6 border-b border-gray-200">
          <Modal.Title className="text-lg sm:text-xl font-semibold text-gray-900">
            Invite Team Members
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 sm:p-6 overflow-y-visible flex-1 relative">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Members
            </label>

            {selectedMembers?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 p-2 sm:p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                {getSelectedMemberNames()?.map(member => (
                  <span
                    key={member.id}
                    className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 bg-[#AEF468] bg-opacity-30 text-gray-800 rounded-full text-xs sm:text-sm border border-[#AEF468]"
                  >
                    <span className="truncate max-w-[150px] sm:max-w-none">{member.name}</span>
                    <button
                      onClick={() => removeMember(member.id)}
                      className="hover:bg-[#AEF468] hover:bg-opacity-50 rounded-full p-0.5 transition-colors flex-shrink-0"
                    >
                      <FiX size={14} />
                    </button>
                  </span >
                ))}
              </div>
            )}

            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <FiSearch
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 flex-shrink-0"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      if (!document.activeElement.closest('.member-dropdown-list')) {
                        setDropdownOpen(false);
                      }
                    }, 100);
                  }}
                  className={`w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 ${FOCUS_RING_COLOR} outline-none text-sm sm:text-base`}
                />
              </div>

              {dropdownOpen && (
                <div
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-48 sm:max-h-64 overflow-y-auto member-dropdown-list"

                >
                  {filteredMembers.length > 0 && (
                    <div
                      onClick={handleToggleAll}
                      className="sticky top-0 bg-gray-100 z-20 px-3 sm:px-4 py-2.5 sm:py-3 cursor-pointer hover:bg-gray-200 border-b border-gray-200 transition-colors flex items-center gap-3 text-sm font-semibold text-gray-700"
                    >
                      <div className="flex-shrink-0 relative">
                        <input
                          type="checkbox"
                          checked={isAllFilteredSelected}
                          ref={el => el && (el.indeterminate = isIndeterminate)}
                          onChange={() => { }}
                          className={`w-4 h-4 rounded cursor-pointer appearance-none border-2 border-gray-400 ${CHECKBOX_COLOR} relative`}
                        />
                        {(isAllFilteredSelected || isIndeterminate) && (
                          <svg
                            className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            {isIndeterminate ? (
                              <rect x="4" y="7" width="8" height="2" fill="black" />
                            ) : (
                              <path
                                d="M13 4L6 11L3 8"
                                stroke="black"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            )}
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {isAllFilteredSelected ? 'Unselect All (Filtered)' : `Select All (${filteredMembers.length} Members)`}
                      </div>
                    </div>
                  )}

                  {filteredMembers.length > 0 ? (
                    filteredMembers.map(member => (
                      <div
                        key={getMemberIdentifier(member)}
                        onClick={() => toggleMember(getMemberIdentifier(member))}
                        className={`px-3 sm:px-4 py-2.5 sm:py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors flex items-center gap-2 sm:gap-3`}
                      >
                        <div className="flex-shrink-0 relative">
                          <input
                            type="checkbox"
                            checked={selectedMembers.includes(getMemberIdentifier(member))}
                            onChange={() => { }}
                            className={`w-4 h-4 rounded cursor-pointer appearance-none border-2 border-gray-300 ${CHECKBOX_COLOR} relative`}
                          />
                          {selectedMembers.includes(getMemberIdentifier(member)) && (
                            <svg
                              className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M13 4L6 11L3 8"
                                stroke="black"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {member.first_name + " " + member.last_name}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 truncate">
                            {member.email}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-6 sm:py-8 text-center text-gray-500 text-sm">
                      No members found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 p-2.5 sm:p-3 bg-gray-50 rounded-lg text-xs sm:text-sm text-gray-600">
            <strong>Selected:</strong> {selectedMembers.length} member(s)
          </div>
        </Modal.Body>

        <Modal.Footer className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={handleSendInvitation}
            disabled={selectedMembers.length === 0}
            className={`px-4 py-2 rounded-lg transition-colors text-sm sm:text-base font-semibold order-1 sm:order-2 ${selectedMembers.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-[#AEF468] text-black hover:bg-[#9DE357]'
              }`}
          >
            Send Invitation
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MemberInvitationModal;
